import { ResolveSsoResponseSchema } from '@rhia/protocol';
import { apiFetch } from '@/lib/api';

/**
 * Super-admin user listing payload returned by GET /api/auth/users.
 * Mirrors apps/cloud-api/src/routes/auth.ts.
 */
export type SuperAdminUser = {
  uid: string;
  email: string | null;
  display_name: string | null;
  is_super_admin: boolean;
  created_at_ms: number;
};

export async function listAllUsers(): Promise<SuperAdminUser[]> {
  const body = await apiFetch<{ users: SuperAdminUser[] }>('/api/auth/users');
  return body.users;
}

/**
 * Signal the cloud-api that custom claims may have changed. Callers should
 * follow up with `firebase.auth().currentUser.getIdToken(true)` to refresh
 * the local token. The cloud-api endpoint itself is a no-op stub today.
 */
export async function refreshClaims(): Promise<void> {
  await apiFetch('/api/auth/refresh-claims', { method: 'POST' });
}

/**
 * Resolve a user's email to a per-tenant SSO provider id, or null if no
 * verified domain mapping exists. This endpoint is public (pre-auth).
 * Returns `saml.<wid>` or `oidc.<wid>` when a domain match is found.
 */
export async function resolveSso(email: string): Promise<string | null> {
  const raw = await apiFetch<unknown>(`/api/auth/sso/resolve?email=${encodeURIComponent(email)}`);
  return ResolveSsoResponseSchema.parse(raw).provider_id;
}
